/**
 * NotificationBell — global bell in the OpsLayout header.
 * - Polls the unread count every 30s (light endpoint).
 * - Opens a dropdown with the latest 50 notifications.
 * - Clicking a notification marks it read + navigates to its link.
 * - "Mark all as read" clears all badges.
 *
 * The bell is fully role-agnostic: every logged-in user sees only their own
 * notifications. Fanning-out logic lives on the backend.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { opsApi } from './OpsAuthContext';

const KIND_COLORS = {
  new_request: 'bg-[#DBE6F6] text-[#294B7B]',
  project_assigned: 'bg-[#E3EDDE] text-[#4A5D23]',
  task_assigned: 'bg-[#DEE7CB] text-[#3F5021]',
  due_soon: 'bg-[#FDF3D6] text-[#8A6A18]',
  idle_warning: 'bg-[#F2E3D0] text-[#7A5A2B]',
  approval_needed: 'bg-[#E9DFF3] text-[#573F7A]',
  approval_granted: 'bg-[#C6DFCF] text-[#1F3D2B]',
  approval_rejected: 'bg-[#F0D0C9] text-[#7A2E22]',
  project_delivered: 'bg-[#C6DFCF] text-[#1F3D2B]',
  project_closed: 'bg-[#D1D1CB] text-[#4A4A45]',
  generic: 'bg-[#EFEAE1] text-[#4A5D23]',
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return 'الآن';
  if (diff < 3600) return `منذ ${Math.floor(diff / 60)} د`;
  if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} س`;
  return then.toLocaleDateString('ar-SA');
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const pollCount = useCallback(async () => {
    try {
      const { data } = await opsApi.get('/notifications/unread-count');
      setUnread(data.unread || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await opsApi.get('/notifications');
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll unread count every 30s.
  useEffect(() => {
    pollCount();
    const t = setInterval(pollCount, 30000);
    return () => clearInterval(t);
  }, [pollCount]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    if (!open) fetchAll();
    setOpen((v) => !v);
  };

  const markAll = async () => {
    await opsApi.post('/notifications/mark-read');
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const openNotification = async (n) => {
    if (!n.read) {
      try {
        await opsApi.post(`/notifications/${n.id}/read`);
      } catch {
        /* ignore */
      }
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    else if (n.project_id) navigate(`/ops/projects/${n.project_id}`);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid="notification-bell"
        onClick={toggle}
        aria-label="الإشعارات"
        className="relative w-10 h-10 rounded-full border border-[#E6E0D6] bg-white hover:bg-[#F3F1E9] flex items-center justify-center transition"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#1F3D2B"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B84A3A] text-white text-[10px] font-bold flex items-center justify-center font-['Noto_Kufi_Arabic']"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute end-0 mt-2 w-[380px] max-h-[520px] overflow-hidden bg-white rounded-2xl ring-1 ring-[#E6E0D6] shadow-2xl z-50 flex flex-col"
        >
          <div className="px-4 py-3 border-b border-[#EFEAE1] flex items-center justify-between">
            <h4 className="font-['Noto_Kufi_Arabic'] text-[14px] font-bold text-[#1F3D2B]">
              الإشعارات
            </h4>
            {unread > 0 && (
              <button
                data-testid="mark-all-read"
                onClick={markAll}
                className="text-[11px] font-['Noto_Kufi_Arabic'] text-[#4A5D23] hover:underline"
              >
                تعليم الكل كمقروء
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <p className="text-center text-[12px] text-[#7A866E] font-['Noto_Kufi_Arabic'] py-6">
                جارٍ التحميل…
              </p>
            )}
            {!loading && items.length === 0 && (
              <p
                data-testid="notifications-empty"
                className="text-center text-[12px] text-[#7A866E] font-['Noto_Kufi_Arabic'] py-8"
              >
                لا توجد إشعارات
              </p>
            )}
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    data-testid={`notification-item-${n.id}`}
                    onClick={() => openNotification(n)}
                    className={`w-full text-start px-4 py-3 border-b border-[#F3F1E9] hover:bg-[#FAFAF7] transition ${
                      n.read ? 'opacity-70' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-['Noto_Kufi_Arabic'] text-[12.5px] font-semibold text-[#1F3D2B] truncate">
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#5C6D49] mt-1 leading-relaxed">
                            {n.body}
                          </p>
                        )}
                      </div>
                      {!n.read && (
                        <span className="mt-1 shrink-0 w-2 h-2 rounded-full bg-[#B84A3A]" />
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span
                        className={`text-[9.5px] px-1.5 py-0.5 rounded-full font-['Noto_Kufi_Arabic'] ${
                          KIND_COLORS[n.kind] || KIND_COLORS.generic
                        }`}
                      >
                        {n.kind}
                      </span>
                      <span className="text-[10.5px] font-['Noto_Kufi_Arabic'] text-[#7A866E]">
                        {fmtWhen(n.created_at)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
